"use client";

import { useCallback, useEffect, useRef } from "react";
import { useTranslations } from "next-intl";
import { useBffAuth } from "@/components/providers/bff-auth-provider";
import { usePathname, useRouter } from "@/i18n/navigation";
import { readDocumentVisible, useDocumentVisible } from "@/hooks/use-document-visible";
import { trpc } from "@/lib/trpc/client";
import { applyUnreadCountToDocumentTitle } from "@/components/notifications/notification-document-title";
import { showNotificationToast } from "@/components/notifications/notification-toast";
import {
  NOTIFICATION_UNREAD_BASELINE_SYNC_EVENT,
  syncNotificationUnreadCountBaseline,
} from "@/components/notifications/notification-unread-baseline";

const VISIBILITY_REFRESH_DEBOUNCE_MS = 300;

type NotificationStreamPayload = {
  type: string;
  recipientId?: string;
  title?: string;
  body?: string;
  webPath?: string | null;
  isRead?: boolean;
  unreadCount?: number;
};

export function NotificationRealtimeProvider({ children }: { children: React.ReactNode }) {
  const auth = useBffAuth();

  // Mid: Bieżąca sesja global-account nie obsługuje starszego transportu powiadomień.
  // Ta granica wybiera transport sesji; nie określa praw konta ani członkostwa w firmie.
  if (auth.user?.profile === "global-account") {
    return <>{children}</>;
  }

  return <LegacyNotificationRealtimeProvider>{children}</LegacyNotificationRealtimeProvider>;
}

function LegacyNotificationRealtimeProvider({ children }: { children: React.ReactNode }) {
  const t = useTranslations("app.notifications");
  const router = useRouter();
  const pathname = usePathname();
  const auth = useBffAuth();
  const isDocumentVisible = useDocumentVisible();
  const utils = trpc.useUtils();
  const lastUnreadCountRef = useRef<number | null>(null);
  const shownToastRecipientIdsRef = useRef<Set<string>>(new Set());
  const suppressCountToastRef = useRef(false);
  const wasVisibleRef = useRef<boolean | null>(null);
  const visibilityRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const unreadCountQuery = trpc.notifications.unreadCount.useQuery(undefined, {
    enabled: auth.isAuthenticated && !auth.isLoading,
    refetchOnWindowFocus: false,
    staleTime: 60_000,
  });
  const unreadCount = unreadCountQuery.data?.count;
  const refetchUnreadCount = unreadCountQuery.refetch;
  const handleMarkReadSuccess = useCallback(
    (nextUnreadCount: number) => {
      syncNotificationUnreadCountBaseline(nextUnreadCount);
      void utils.notifications.list.invalidate();
      void utils.notifications.unreadCount.invalidate();
    },
    [utils],
  );

  const markReadMutation = trpc.notifications.markRead.useMutation({
    onSuccess: (result) => handleMarkReadSuccess(result.unreadCount),
  });

  const rememberToastRecipient = useCallback((recipientId: string) => {
    const shown = shownToastRecipientIdsRef.current;
    shown.add(recipientId);
    if (shown.size > 50) {
      const oldest = shown.values().next().value;
      if (oldest) {
        shown.delete(oldest);
      }
    }
  }, []);

  const showCreatedNotificationToast = useCallback(
    (payload: { recipientId: string; title: string; body?: string }) => {
      if (shownToastRecipientIdsRef.current.has(payload.recipientId)) {
        return;
      }

      rememberToastRecipient(payload.recipientId);
      suppressCountToastRef.current = true;

      return showNotificationToast({
        title: payload.title,
        description: payload.body,
        openLabel: t("toastOpen"),
        markReadLabel: t("toastMarkRead"),
        onOpen: () => {
          router.push("/notifications");
        },
        onMarkRead: () => {
          markReadMutation.mutate({ recipientId: payload.recipientId });
        },
      });
    },
    [markReadMutation, rememberToastRecipient, router, t],
  );

  const showCreatedNotificationToastRef = useRef(showCreatedNotificationToast);
  showCreatedNotificationToastRef.current = showCreatedNotificationToast;
  const utilsRef = useRef(utils);
  utilsRef.current = utils;
  const genericToastContextRef = useRef({ t, router });
  genericToastContextRef.current = { t, router };

  useEffect(() => {
    if (auth.isLoading) {
      return;
    }

    if (!auth.isAuthenticated) {
      applyUnreadCountToDocumentTitle(0);
      return;
    }

    applyUnreadCountToDocumentTitle(unreadCount);
  }, [auth.isAuthenticated, auth.isLoading, pathname, unreadCount]);

  useEffect(() => {
    if (auth.isLoading || !auth.isAuthenticated) {
      return;
    }

    const titleElement = document.querySelector("title");
    if (!titleElement) {
      return;
    }

    const syncTitle = () => {
      applyUnreadCountToDocumentTitle(unreadCount);
    };

    const observer = new MutationObserver(syncTitle);
    observer.observe(titleElement, {
      childList: true,
      characterData: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
    };
  }, [auth.isAuthenticated, auth.isLoading, unreadCount]);

  useEffect(() => {
    const syncBaseline = (event: Event) => {
      const count = (event as CustomEvent<{ count: number }>).detail?.count;
      if (typeof count === "number") {
        lastUnreadCountRef.current = count;
      }
    };

    window.addEventListener(NOTIFICATION_UNREAD_BASELINE_SYNC_EVENT, syncBaseline);
    return () => {
      window.removeEventListener(NOTIFICATION_UNREAD_BASELINE_SYNC_EVENT, syncBaseline);
    };
  }, []);

  useEffect(() => {
    if (auth.isLoading || !auth.isAuthenticated) {
      lastUnreadCountRef.current = null;
      return;
    }

    const nextCount = unreadCount;
    if (typeof nextCount !== "number") {
      return;
    }

    const previousCount = lastUnreadCountRef.current;
    lastUnreadCountRef.current = nextCount;
    if (previousCount === null || nextCount <= previousCount) {
      return;
    }

    if (suppressCountToastRef.current) {
      suppressCountToastRef.current = false;
      return;
    }

    const newCount = nextCount - previousCount;
    let disposed = false;

    void (async () => {
      try {
        const page = await utilsRef.current.notifications.list.fetch({
          limit: 1,
          readState: "unread",
        });
        if (disposed) return;
        const latest = page.items[0];
        if (latest && !shownToastRecipientIdsRef.current.has(latest.recipientId)) {
          showCreatedNotificationToastRef.current({
            recipientId: latest.recipientId,
            title: latest.title,
            body: latest.body,
          });
          return;
        }
      } catch {
        // Fall through to generic toast below.
      }

      if (disposed) return;
      const { t, router } = genericToastContextRef.current;
      showNotificationToast({
        title: t("toastNewTitle"),
        description: t("toastNewDescription", { count: newCount }),
        openLabel: t("toastOpen"),
        onOpen: () => {
          router.push("/notifications");
        },
      });
    })();

    return () => {
      disposed = true;
    };
  }, [auth.isAuthenticated, auth.isLoading, auth.user?.id, unreadCount]);

  useEffect(() => {
    if (auth.isLoading || !auth.isAuthenticated) {
      return;
    }

    void refetchUnreadCount();
  }, [auth.isAuthenticated, auth.isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (auth.isLoading || !auth.isAuthenticated || !isDocumentVisible) {
      return;
    }

    const eventSource = new EventSource("/api/notifications/stream", {
      withCredentials: true,
    });

    const handleEvent = (event: MessageEvent<string>) => {
      const notifications = utilsRef.current.notifications;
      try {
        const payload = JSON.parse(event.data) as NotificationStreamPayload;
        const eventType = payload.type || event.type;
        void notifications.list.invalidate();

        if (eventType === "notification.created") {
          if (payload.recipientId && payload.title) {
            showCreatedNotificationToastRef.current({
              recipientId: payload.recipientId,
              title: payload.title,
              body: payload.body,
            });
          }

          if (typeof payload.unreadCount === "number") {
            lastUnreadCountRef.current = payload.unreadCount;
            notifications.unreadCount.setData(undefined, { count: payload.unreadCount });
          } else {
            void notifications.unreadCount.invalidate();
          }

          return;
        }

        if (
          eventType === "notification.read-state-changed" ||
          eventType === "unread-count.changed"
        ) {
          if (typeof payload.unreadCount === "number") {
            lastUnreadCountRef.current = payload.unreadCount;
            notifications.unreadCount.setData(undefined, { count: payload.unreadCount });
          } else {
            void notifications.unreadCount.invalidate();
          }
          return;
        }

        if (typeof payload.unreadCount === "number") {
          notifications.unreadCount.setData(undefined, { count: payload.unreadCount });
        } else {
          void notifications.unreadCount.invalidate();
        }
      } catch {
        void notifications.list.invalidate();
        void notifications.unreadCount.invalidate();
      }
    };

    eventSource.addEventListener("notification.created", handleEvent);
    eventSource.addEventListener("notification.read-state-changed", handleEvent);
    eventSource.addEventListener("unread-count.changed", handleEvent);

    return () => {
      eventSource.close();
    };
  }, [auth.isAuthenticated, auth.isLoading, isDocumentVisible]);

  // Refresh once per hidden -> visible edge, debounced: rapid tab switching must not turn into a
  // burst of invalidations, and `focus` + `visibilitychange` would otherwise both fire per return.
  useEffect(() => {
    if (auth.isLoading || !auth.isAuthenticated) {
      wasVisibleRef.current = null;
      return;
    }

    if (wasVisibleRef.current === null) {
      wasVisibleRef.current = readDocumentVisible();
      return;
    }

    const wasVisible = wasVisibleRef.current;
    wasVisibleRef.current = isDocumentVisible;

    if (!wasVisible && isDocumentVisible) {
      if (visibilityRefreshTimerRef.current) {
        clearTimeout(visibilityRefreshTimerRef.current);
      }
      visibilityRefreshTimerRef.current = setTimeout(() => {
        visibilityRefreshTimerRef.current = null;
        void utilsRef.current.notifications.unreadCount.invalidate();
        void utilsRef.current.notifications.list.invalidate();
      }, VISIBILITY_REFRESH_DEBOUNCE_MS);
    }

    return () => {
      if (visibilityRefreshTimerRef.current) {
        clearTimeout(visibilityRefreshTimerRef.current);
        visibilityRefreshTimerRef.current = null;
      }
    };
  }, [auth.isAuthenticated, auth.isLoading, isDocumentVisible]);

  useEffect(() => {
    if (auth.isLoading || !auth.isAuthenticated || !isDocumentVisible) {
      return;
    }

    const refreshNotifications = () => {
      void utilsRef.current.notifications.unreadCount.invalidate();
      void utilsRef.current.notifications.list.invalidate();
    };

    window.addEventListener("online", refreshNotifications);

    return () => {
      window.removeEventListener("online", refreshNotifications);
    };
  }, [auth.isAuthenticated, auth.isLoading, isDocumentVisible]);

  return <>{children}</>;
}
